import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Loading } from '@leapeasy/ui-kit';
import { useNavigate } from 'react-router-dom';
import { getPolicyCancelAction } from 'store/actions/propertyActions';
import { openDetails } from 'store/actions/uiActions';
import { failedNotif } from 'store/actions/notifActions';
import { getFlaggedCancelReportAction } from 'store/actions/reportActions';
import { FlaggedCancellationTable } from 'components/Tables/FlaggedCancellation';
import { getPolicy } from 'store/sagas/APIs/property';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { usePaginatedListFetch } from 'hooks/usePaginatedListFetch';
import { useLazyExportDatasetTotal } from 'hooks/useLazyExportDatasetTotal';
import { fetchAllPaginated } from 'utils/exportPagination';
import { ApplicationListActiveFilters } from 'components/ApplicationListActiveFilters/ApplicationListActiveFilters';
import {
  buildFlaggedCancellationListFilterChips,
  parseFlaggedCancellationListFilterChipId,
} from 'utils/propertyListActiveFilterChips';
import {
  DEFAULT_FLAGGED_CANCELLATION_LIST_EXTRA_FILTERS,
  areArraysEqual,
  parseFlaggedCancellationFilters,
  serializeFlaggedCancellationFilters,
} from 'utils/flaggedCancellationListQueryFilters';
import {
  POLICY_LIST_APP_TYPE_FILTER_OPTIONS,
  POLICY_FLAG_FILTER_OPTIONS,
  POLICY_STATUS_FILTER_OPTIONS,
} from 'data/constants/common_constants';
const EMPTY_FLAGGED_CANCEL_FILTER = {
  searchText: '',
  options: {},
  selectedLeaseDate: {},
  selectedFlg: [],
  selectedAppType: [],
  selectedPolicyStatus: [],
};

export const FlaggedCancellations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search ...',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: DEFAULT_FLAGGED_CANCELLATION_LIST_EXTRA_FILTERS,
    parseExtraFilters: parseFlaggedCancellationFilters,
    serializeExtraFilters: serializeFlaggedCancellationFilters,
  });
  const [selectedFlg, setSelectedFlg] = useState([]);
  const [selectedAppType, setSelectedAppType] = useState([]);
  const [selectedPolicyStatus, setSelectedPolicyStatus] = useState([]);
  const [selectedFlgDraft, setSelectedFlgDraft] = useState([]);
  const [selectedAppTypeDraft, setSelectedAppTypeDraft] = useState([]);
  const [selectedPolicyStatusDraft, setSelectedPolicyStatusDraft] = useState([]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedFlg = Array.isArray(extraFilters.selectedFlg) ? extraFilters.selectedFlg : [];
    const nextSelectedAppType = Array.isArray(extraFilters.selectedAppType)
      ? extraFilters.selectedAppType
      : [];
    const nextSelectedPolicyStatus = Array.isArray(extraFilters.selectedPolicyStatus)
      ? extraFilters.selectedPolicyStatus
      : [];
    setSelectedFlg((prev) => (areArraysEqual(prev, nextSelectedFlg) ? prev : nextSelectedFlg));
    setSelectedAppType((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedPolicyStatus((prev) =>
      areArraysEqual(prev, nextSelectedPolicyStatus) ? prev : nextSelectedPolicyStatus,
    );
    setSelectedFlgDraft((prev) => (areArraysEqual(prev, nextSelectedFlg) ? prev : nextSelectedFlg));
    setSelectedAppTypeDraft((prev) =>
      areArraysEqual(prev, nextSelectedAppType) ? prev : nextSelectedAppType,
    );
    setSelectedPolicyStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedPolicyStatus) ? prev : nextSelectedPolicyStatus,
    );
  }, [extraFilters]);

  const flaggedCancellations = useSelector((state) => state.getIn(['property', 'policy-cancel']));
  const successMsg = useSelector((state) => state.getIn(['notification', 'successMsg']));
  const successText = successMsg == null ? '' : String(successMsg);

  useEffect(() => {
    dispatch(getFlaggedCancelReportAction());
  }, [dispatch]);

  const listQuery = useMemo(
    () => ({
      filter: {
        ...filter,
        selectedLeaseDate: {},
        selectedFlg,
        selectedAppType,
        selectedPolicyStatus,
      },
      offset: pagination.pageNumber * pagination.rowsPerPage,
      limit: pagination.rowsPerPage,
      sortOptions,
    }),
    [
      filter,
      selectedFlg,
      selectedAppType,
      selectedPolicyStatus,
      pagination.pageNumber,
      pagination.rowsPerPage,
      sortOptions,
    ],
  );

  usePaginatedListFetch({
    enabled: true,
    action: getPolicyCancelAction,
    payload: listQuery,
  });

  const refetchList = useCallback(() => {
    dispatch(getPolicyCancelAction(listQuery));
  }, [dispatch, listQuery]);

  const fetchFullExportDatasetTotal = useCallback(async () => {
    const response = await getPolicy({
      filter: EMPTY_FLAGGED_CANCEL_FILTER,
      cancel: true,
      offset: 0,
      limit: 1,
      sortOptions,
    });
    return response?.total != null ? Number(response.total) : undefined;
  }, [sortOptions]);

  const {
    fullExportDatasetTotal,
    isLoadingFullExportTotal,
    loadFullExportDatasetTotal,
    resetFullExportDatasetTotal,
  } = useLazyExportDatasetTotal(fetchFullExportDatasetTotal);

  useEffect(() => {
    resetFullExportDatasetTotal();
  }, [sortOptions, resetFullExportDatasetTotal]);

  useEffect(() => {
    if (!successText || successText === 'loading') return;
    if (!successText.toLowerCase().includes('cancelled')) return;
    refetchList();
    dispatch(getFlaggedCancelReportAction());
  }, [successText, dispatch, refetchList]);

  const POLICYFLAG = POLICY_FLAG_FILTER_OPTIONS;

  const APPTYPE = POLICY_LIST_APP_TYPE_FILTER_OPTIONS;

  const POLICY_STATUS = POLICY_STATUS_FILTER_OPTIONS;

  const onClickCancelPolicies = useCallback(
    (flaggedCancellation) => {
      const policyId = flaggedCancellation?.app_id;
      if (!policyId) {
        dispatch(failedNotif('Flagged cancellation detail ID is missing.'));
        return;
      }

      dispatch(
        openDetails({
          type: 'flagged-cancellation',
          data: flaggedCancellation,
        }),
      );

      navigate(`/property/cancel-policies/${policyId}/detail`);
    },
    [dispatch, navigate],
  );

  const applyFunc = useCallback(() => {
    setSelectedFlg(selectedFlgDraft);
    setSelectedAppType(selectedAppTypeDraft);
    setSelectedPolicyStatus(selectedPolicyStatusDraft);
    setExtraFilters({
      selectedFlg: selectedFlgDraft,
      selectedAppType: selectedAppTypeDraft,
      selectedPolicyStatus: selectedPolicyStatusDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [
    selectedFlgDraft,
    selectedAppTypeDraft,
    selectedPolicyStatusDraft,
    setExtraFilters,
    setPagination,
  ]);
  const resetFunc = useCallback(() => {
    setSelectedFlg([]);
    setSelectedAppType([]);
    setSelectedPolicyStatus([]);
    setSelectedFlgDraft([]);
    setSelectedAppTypeDraft([]);
    setSelectedPolicyStatusDraft([]);
    setExtraFilters({
      selectedFlg: [],
      selectedAppType: [],
      selectedPolicyStatus: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const flaggedCancellationsJSON = flaggedCancellations && flaggedCancellations.toJS();

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(flaggedCancellationsJSON?.total) || 0,
    isLoading: Boolean(flaggedCancellationsJSON?.isLoading),
  });

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(flaggedCancellationsJSON?.total) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: {
              ...filter,
              selectedLeaseDate: {},
              selectedFlg,
              selectedAppType,
              selectedPolicyStatus,
            },
            cancel: true,
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export flagged cancellations right now.');
    }
  }, [
    flaggedCancellationsJSON?.total,
    filter,
    selectedFlg,
    selectedAppType,
    selectedPolicyStatus,
    sortOptions,
  ]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords =
      Number(fullExportDatasetTotal) || Number(await loadFullExportDatasetTotal()) || 0;
    if (totalRecords === 0) return [];

    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getPolicy({
            filter: EMPTY_FLAGGED_CANCEL_FILTER,
            cancel: true,
            offset,
            limit,
            sortOptions,
          }),
      });
    } catch (error) {
      throw new Error('Unable to export full flagged cancellations dataset right now.');
    }
  }, [fullExportDatasetTotal, loadFullExportDatasetTotal, sortOptions]);

  const listFilterChips = useMemo(
    () =>
      buildFlaggedCancellationListFilterChips({
        selectedFlg,
        selectedAppType,
        selectedPolicyStatus,
        flagOptions: POLICYFLAG,
        appTypeOptions: APPTYPE,
        policyStatusOptions: POLICY_STATUS,
      }),
    [selectedFlg, selectedAppType, selectedPolicyStatus, POLICYFLAG, APPTYPE, POLICY_STATUS],
  );

  const handleRemoveFlaggedCancellationListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseFlaggedCancellationListFilterChipId(chipId);
      if (!parsed) return;

      if (parsed.type === 'flag') {
        const next = selectedFlg.filter((s) => s !== parsed.value);
        setSelectedFlg(next);
        setSelectedFlgDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedFlg: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'appType') {
        const next = selectedAppType.filter((s) => s !== parsed.value);
        setSelectedAppType(next);
        setSelectedAppTypeDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedAppType: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
        return;
      }

      if (parsed.type === 'policyStatus') {
        const next = selectedPolicyStatus.filter((s) => s !== parsed.value);
        setSelectedPolicyStatus(next);
        setSelectedPolicyStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedPolicyStatus: next }));
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      }
    },
    [selectedFlg, selectedAppType, selectedPolicyStatus, setExtraFilters, setPagination],
  );

  const listFilterActiveSummary = useMemo(() => {
    if (!listFilterChips.length) return null;
    return (
      <ApplicationListActiveFilters
        chips={listFilterChips}
        onRemoveChip={handleRemoveFlaggedCancellationListFilterChip}
        onClearAll={resetFunc}
      />
    );
  }, [listFilterChips, handleRemoveFlaggedCancellationListFilterChip, resetFunc]);

  return (
    <DashboardLayoutContainer>
      {flaggedCancellations ? (
        <React.Fragment>
          <CustomBox>
            <FlaggedCancellationTable
              flaggedCancellations={flaggedCancellationsJSON.data}
              loading={flaggedCancellationsJSON.isLoading}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              onLoadFullDatasetTotal={loadFullExportDatasetTotal}
              isLoadingFullDatasetTotal={isLoadingFullExportTotal}
              dropFilterActiveSummary={listFilterActiveSummary}
              dropFilterActiveCount={listFilterChips.length}
              filter={filter}
              onChangeFilter={setFilter}
              pagination={{
                ...pagination,
                totalItems: flaggedCancellationsJSON.total,
                totalPages: Math.ceil(flaggedCancellationsJSON.total / pagination.rowsPerPage),
              }}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedFlgDraft(e.target.value);
                      }}
                      options={[...POLICYFLAG]}
                      value={selectedFlgDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Flags"
                      placeholderFlag={false}
                      label="Flags"
                      style={{
                        width: '100%',
                      }}
                    />

                    <Dropdown
                      onChange={(e) => {
                        setSelectedPolicyStatusDraft(e.target.value);
                      }}
                      options={[...POLICY_STATUS]}
                      value={selectedPolicyStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Types"
                      placeholderFlag={false}
                      label="Policy Status"
                      style={{
                        width: '100%',
                      }}
                    />

                    <Dropdown
                      onChange={(e) => {
                        setSelectedAppTypeDraft(e.target.value);
                      }}
                      options={[...APPTYPE]}
                      value={selectedAppTypeDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder="All Types"
                      placeholderFlag={true}
                      label="Application Type"
                      style={{
                        width: '100%',
                      }}
                    />
                  </DropFilterContainer>
                ),
                applyFunc: applyFunc,
                resetFunc: resetFunc,
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickCancelPolicies={onClickCancelPolicies}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;
